import React from 'react'
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Chip, Skeleton,
  Typography, Alert, FormControlLabel, Radio,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useRecordMetadata } from '../../hooks/useRecordMetadata'
import { toggleMetadataValue } from './metadataFilterState'
import { sidebarMetadataKeys } from './platformFilter'

interface MetadataFilterSectionProps {
  customer: string
  job: string
  /**
   * A Sky product slug. When given, the facets come from the product-scoped
   * mount, so the values offered are the ones this department actually has.
   * Without it a product sidebar would offer job-wide values — filters that
   * silently match nothing here (T2.3).
   */
  product?: string
  selected: Record<string, string>
  onChange: (next: Record<string, string>) => void
  disabled?: boolean
  /**
   * The curated list of keys this mount offers, in the order it wants them.
   * Omitted, the section DISCOVERS every key the mount has (minus the
   * toolbar-owned ones) — right for an admin surface exploring unfamiliar data,
   * wrong for a product dashboard, where discovery surfaces per-record ids as
   * if they were filters. Sky passes `SKY_SIDEBAR_METADATA_KEYS`, which is
   * empty, so nothing renders at all.
   */
  allowKeys?: readonly string[]
}

// Record-metadata facets (region, ...) for the social dashboard.
// Values come from the job-level metadata summary; selection is single-value
// per key (record metadata is one value per key).
//
// `author_type` and `source` are deliberately NOT offered here: the always-on
// toolbar (PlatformFilterBar) owns both, and the toolbar wins the merge in
// buildLabelQuery. Two controls over one key can only ever disagree silently.
const MetadataFilterSection: React.FC<MetadataFilterSectionProps> = ({
  customer, job, product, selected, onChange, disabled, allowKeys,
}) => {
  // 🔴 AN EMPTY ALLOWLIST MUST ALSO TURN THE REQUEST OFF, not just the render.
  //
  // Hooks cannot be called conditionally, so this ran before the `return null`
  // below and fetched facets nobody would ever see. The endpoint behind it is
  // the job-wide facet summary, which explodes every record's metadata document
  // and ranks ~496,000 distinct (key, value) pairs through an on-disk sort —
  // 4.1-4.9s on a production clone, the slowest query on the page — so a Sky
  // product dashboard paid all of it to render nothing at all.
  //
  // `undefined` is NOT the same as `[]` here and the distinction is load-
  // bearing: an admin mount passes no allowlist and DISCOVERS its keys, so it
  // genuinely needs the answer. Only an explicitly EMPTY list means "this mount
  // offers no metadata facets", and only that case is safe to skip.
  const offersNoFacets = allowKeys !== undefined && allowKeys.length === 0
  const { metadata, isLoading, error } = useRecordMetadata({
    customer, job, product, enabled: !offersNoFacets,
  })
  const keys = sidebarMetadataKeys(Object.keys(metadata), allowKeys)

  // An EMPTY allowlist is a decision — this mount offers no metadata facets —
  // so it renders nothing, ahead of the loading and error states. A skeleton or
  // a "failed to load facets" warning for a section that was never going to
  // appear is noise about a request whose answer nobody is waiting for.
  if (offersNoFacets) return null
  if (isLoading) return <Skeleton variant="rounded" height={48} sx={{ mt: 1 }} />
  if (error) return <Alert severity="warning" sx={{ mt: 1 }}>Failed to load metadata facets</Alert>
  if (keys.length === 0) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Record metadata
      </Typography>
      {keys.map((key) => (
        <Accordion key={key} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight: selected[key] ? 600 : 400 }}>
              {key}
              {selected[key] && (
                <Chip size="small" color="info" label={selected[key]} sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {(metadata[key] ?? []).map(({ value, count }) => (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Radio
                      size="small"
                      color="info"
                      checked={selected[key] === value}
                      onClick={() => !disabled && onChange(toggleMetadataValue(selected, key, value))}
                    />
                  }
                  label={<Typography variant="body2">{value}</Typography>}
                  disabled={disabled}
                />
                <Chip size="small" variant="outlined" label={count} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

export default MetadataFilterSection
